/**
 * @file Fin de session côté client : nettoyage unique de tout ce qui est persisté.
 * @module packages.common.utils.sessionTermination
 *
 * Une session se termine de deux manières : l'utilisateur se déconnecte, ou le
 * serveur déclare la session finie. Le nettoyage doit être identique dans les
 * deux cas, sans quoi il reste des traces d'une session morte (cache de données,
 * établissement sélectionné, état de session) que la connexion suivante hérite.
 *
 * C'est ici, et nulle part ailleurs, que l'on décide de ce qu'une fin de session
 * efface.
 */

import {mutate} from 'swr';
import {clearSelectedCompany} from './companyStorage';
import {SESSION_STORAGE_KEYS, RECENTLY_LOGGED_OUT_KEY, PRE_AUTH_PATH_KEY} from './sessionStorageKeys';
import useSessionStore from '../stores/sessionStore';
import {paths} from '../routes/paths';
import {TERMINATION_REASON} from '../stores/sessionStatus';

// Plusieurs requêtes parallèles reçoivent le même refus : la session ne doit
// être terminée qu'une fois, sinon le nettoyage et la redirection se bousculent.
//
// Ce verrou ne protège que cette rafale, et il est donc borné dans le temps. Le
// lier au rechargement de la page serait un pari perdant : une navigation n'est
// qu'une demande, et l'éditeur de plan de salle ou le calendrier wellness
// arment un `beforeunload` bloquant dès qu'il y a des modifications non
// enregistrées. Si l'utilisateur répond « Rester sur la page », le rechargement
// n'a pas lieu. Un verrou laissé armé condamnerait alors l'onglet : plus aucune
// fin de session ne serait possible, et le bouton « Réessayer » tournerait en
// rond jusqu'au rechargement manuel, peu évident sur une PWA plein écran.
const TERMINATION_LOCK_MS = 2000;

let terminating = false;

/**
 * Met fin à la session locale.
 *
 * Les deux motifs ne se traitent pas pareil. Une session qui expire sera
 * reprise par la même personne : on mémorise sa page pour l'y ramener après
 * reconnexion. Une déconnexion volontaire, elle, signifie presque toujours
 * qu'on passe la main : la personne suivante n'a pas à hériter de la page de
 * la précédente sur une tablette partagée.
 *
 * @param {Object} [options] - Options de terminaison
 * @param {string} [options.reason] - Un motif de `TERMINATION_REASON` (par défaut : session refusée par le serveur)
 * @return {void}
 */
export const terminateSession = ({reason = TERMINATION_REASON.EXPIRED} = {}) => {
  if (terminating) {
    return;
  }
  terminating = true;

  const voluntary = reason === TERMINATION_REASON.LOGOUT;
  const currentPath = window.location.pathname + window.location.search;

  try {
    SESSION_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    localStorage.setItem(RECENTLY_LOGGED_OUT_KEY, 'true');

    if (!voluntary && currentPath !== paths.auth.login) {
      localStorage.setItem(PRE_AUTH_PATH_KEY, currentPath);
    }
  } catch (error) {
    // Stockage indisponible (navigation privée, quota) : le reste du nettoyage
    // doit aboutir malgré tout.
  }

  clearSelectedCompany();

  // Statut terminal, et non `idle` : sur `idle` le provider relancerait un
  // chargement de session pendant que la navigation se prépare, et l'écran
  // d'erreur s'afficherait par-dessus la page de connexion qui arrive.
  useSessionStore.getState().close();

  // Purge du cache SWR sans revalidation : les requêtes repartiraient sans session.
  mutate(() => true, undefined, {revalidate: false});

  // Le rechargement complet, préféré à une navigation applicative, garantit
  // qu'aucun état React ni aucune requête en vol ne survit à une session
  // refusée. Un départ volontaire n'en a pas besoin : le contexte React est
  // remis à zéro par `logout`, et les guards font la navigation.
  const leaveForLogin = () => {
    if (voluntary || window.location.pathname === paths.auth.login) {
      return;
    }
    window.location.href = paths.auth.login;
  };

  // Le verrou retombe dans tous les cas, et la navigation est redemandée avec
  // lui : une navigation n'est qu'une demande, et un écran à modifications non
  // enregistrées arme un `beforeunload` que l'utilisateur peut refuser. Sans
  // cette seconde chance, l'onglet resterait sur un écran de chargement sans
  // issue, session finie et personne pour le dire. À cet instant l'arbre
  // applicatif est démonté, donc plus aucun `beforeunload` n'est armé et la
  // demande aboutit. Ce délai ne s'écoule jamais quand la première réussit :
  // le contexte est détruit avant.
  setTimeout(() => {
    terminating = false;
    leaveForLogin();
  }, TERMINATION_LOCK_MS);

  leaveForLogin();
};
